import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import { Control, FieldErrors, RegisterOptions } from 'react-hook-form';

import { ICreateWorkerFormData } from 'forms/worker';
import { HelpIcon } from 'components/shared/HelpIcon/HelpIcon';
import { RelationshipController } from 'components/shared/RelationshipController/RelationshipController';

interface ICreateWorkerNextOfKinProps {
    register: (options?: RegisterOptions) => React.Ref<HTMLInputElement>;
    errors: FieldErrors<ICreateWorkerFormData>;
    control: Control;
    disabled?: boolean;
    loadRecaptcha: () => void;
}

export const CreateWorkerNextOfKin = (
    {register, errors, control, disabled, loadRecaptcha}: ICreateWorkerNextOfKinProps
) => {
    const {t} = useTranslation();

    return (
        <div>
            <div className="form__section-header">
                <Form.Label className="form-label--title">{t('createWorker.nextOfKin.title', 'Next of Kin Details')}</Form.Label>
            </div>
            <Form.Group controlId="nextOfKin.nokRelationship">
                <Form.Label className="form-label--smaller">
                    {t('common.relationshipToWorker', 'Relationship to You')}
                </Form.Label>
                <HelpIcon id="CreateWorker__nextOfKinEmailAddress" className="ml-2">
                        <Trans i18nKey="createWorker.tooltips.relationshipToWorker">
                            The relationship you have with the next of kin. This field is required.
                        </Trans>
                </HelpIcon>
                <RelationshipController
                    name="nextOfKin.nokRelationship"
                    control={control}
                    size="lg"
                    disabled={disabled}
                />
                {(errors?.nextOfKin?.nokRelationship) && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                        {t('errors.relationship.missing', 'Relationship type is required')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <Row>
                <Col sm={6}>
                    <Form.Group controlId="nextOfKin.nokFirstName">
                        <Form.Label className="form-label--smaller">
                            {t('common.firstName', 'First Name')}
                        </Form.Label>
                        <Form.Control
                            name="nextOfKin.nokFirstName"
                            type="text"
                            placeholder={t('common.firstName', 'First Name')}
                            ref={register()}
                            isInvalid={!!errors?.nextOfKin?.nokFirstName}
                            size="lg"
                            disabled={disabled}
                            onClick={() => loadRecaptcha()}
                        />
                        {(errors?.nextOfKin?.nokFirstName) && (
                            <Form.Control.Feedback type="invalid">
                                {t('errors.firstName.missing', 'You must enter a valid first name')}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
                <Col sm={6}>
                    <Form.Group controlId="nextOfKin.nokLastName">
                        <Form.Label className="form-label--smaller">
                            {t('common.lastName', 'Last Name')}
                        </Form.Label>
                        <Form.Control
                            name="nextOfKin.nokLastName"
                            type="text"
                            placeholder={t('common.lastName', 'Last Name')}
                            ref={register()}
                            isInvalid={!!errors?.nextOfKin?.nokLastName}
                            size="lg"
                            disabled={disabled}
                            onClick={() => loadRecaptcha()}
                        />
                        {(errors?.nextOfKin?.nokLastName) && (
                            <Form.Control.Feedback type="invalid">
                                {t('errors.lastName.missing', 'You must enter a valid last name')}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <Form.Group controlId="nextOfKin.nokEmail">
                <Form.Label className="form-label--smaller">
                    {t('common.emailAddress', 'Email Address')}
                    <HelpIcon id="CreateWorker__nextOfKinEmailAddress" className="ml-2">
                        <Trans i18nKey="createWorker.tooltips.nextOfKinEmail">
                            The email address of the next of kin contact person. This field is required.
                        </Trans>
                    </HelpIcon>
                </Form.Label>
                <Form.Control
                    name="nextOfKin.nokEmail"
                    type="email"
                    placeholder={t('common.emailAddress', 'Email Address')}
                    ref={register()}
                    isInvalid={!!errors?.nextOfKin?.nokEmail}
                    size="lg"
                    disabled={disabled}
                    onClick={() => loadRecaptcha()}
                />
                {(errors?.nextOfKin?.nokEmail) && (
                    <Form.Control.Feedback type="invalid">
                        {t('errors.email.missing', 'You must enter a valid email address')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group controlId="nextOfKin.nokPhoneNumber">
                <Form.Label className="form-label--smaller">
                    {t('common.mobileNumber', 'Mobile Number')}
                    <HelpIcon id="createWorker.nextOfKin.phone.helpIcon" className="ml-2">
                        <Trans i18nKey="createWorker.tooltips.nextOfKinPhone">
                            The mobile phone number of the next of kin contact person. This field is required.
                        </Trans>
                    </HelpIcon>
                </Form.Label>
                <Form.Control
                    size="lg"
                    type="text"
                    placeholder={t('common.mobileNumber', 'Mobile Number')}
                    name="nextOfKin.nokPhoneNumber"
                    ref={register()}
                    isInvalid={!!errors?.nextOfKin?.nokPhoneNumber}
                    disabled={disabled}
                    onClick={() => loadRecaptcha()}
                />
                {(errors?.nextOfKin?.nokPhoneNumber) && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                        {t('errors.mobilePhone.missing', 'Mobile number is required')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
        </div>
    );
};
